import { getAIModelById, getDefaultAIModel } from './aiModelService';
import { deductCredits } from './optimizedCreditService';
import { hasEnoughCreditsForAIModel } from './creditService';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, increment } from 'firebase/firestore';

/**
 * Compare a product sizechart with a master sizechart template
 * @param {string} userId - The user ID
 * @param {string} shopId - The shop ID
 * @param {Object} requestData - The request data containing all parameters
 * @returns {Promise<Object>} - The comparison result
 */
export const compareSizecharts = async (requestData) => {
  const { shopId, userId, masterSizechartId, productSizechartData, modelId, countries, prompt } = requestData;
  try {
    // Get the master sizechart data if we only have the ID
    let masterSizechart = null;
    if (masterSizechartId) {
      // Fetch the master sizechart from Firestore - it's in a subcollection under shops
      // We need to get the shopId from the request
      if (!shopId) {
        throw new Error('Shop ID is required to fetch master sizechart');
      }
      
      const sizechartRef = doc(db, `shops/${shopId}/sizecharts`, masterSizechartId);
      const sizechartDoc = await getDoc(sizechartRef);
      
      if (!sizechartDoc.exists()) {
        throw new Error(`Master sizechart with ID ${masterSizechartId} not found`);
      }
      
      masterSizechart = {
        id: sizechartDoc.id,
        ...sizechartDoc.data()
      };
    }
    
    // Get the AI model to use
    let model = null;
    if (modelId) {
      model = await getAIModelById(modelId);
    }
    
    if (!model) {
      // Fall back to default model if specified model not found
      model = await getDefaultAIModel();
    }
    
    if (!model) {
      throw new Error('No AI model available for sizechart comparison');
    }
    
    // Check if user has enough credits
    const creditCost = model.creditCostPerOutput || 1;
    const hasCredits = await hasEnoughCreditsForAIModel(userId, creditCost);
    
    if (!hasCredits) {
      throw new Error(`Not enough credits. You need ${creditCost} credits to perform this comparison.`);
    }
    
    // Use the provided prompt if available, otherwise generate one
    const aiPrompt = prompt || generateSizechartComparisonPrompt(
      masterSizechart, 
      productSizechartData, 
      3, // Default allowed deviation
      countries || ['US']
    );
    
    // Call the appropriate AI provider based on the model
    let result;
    switch (model.provider) {
      case 'openai':
        result = await callOpenAI(model, aiPrompt);
        break;
      case 'deepseek':
        result = await callDeepSeek(model, aiPrompt);
        break;
      case 'gemini':
        result = await callGemini(model, aiPrompt);
        break;
      default:
        throw new Error(`Unsupported AI provider: ${model.provider}`);
    }
    
    // Parse the result
    const parsedResult = parseAIResponse(result);
    
    // Deduct credits for the operation
    await deductCredits(userId, creditCost, {
      shopId,
      type: 'sizechart_comparison',
      modelId: model.id,
      modelName: model.displayName
    });
    
    // Update shop metrics to track size chart comparison (counts as a generation)
    if (shopId) {
      try {
        const shopRef = doc(db, 'shops', shopId);
        await updateDoc(shopRef, {
          'metrics.sizechartsGenerated': increment(1)
        });
        console.log(`Updated size charts generated count for shop ${shopId} (comparison operation)`);
      } catch (error) {
        console.error('Error updating size charts generated count:', error);
        // Don't throw the error here to avoid interrupting the main flow
      }
    }
    
    return parsedResult;
  } catch (error) {
    console.error('Error comparing sizecharts:', error);
    throw error;
  }
};

/**
 * Generate a prompt for sizechart comparison
 * @param {Object} masterSizechart - The master sizechart template
 * @param {Object} productSizechart - The product sizechart to compare
 * @param {number} allowedDeviation - The allowed deviation in cm
 * @param {Array<string>} targetCountries - The target countries for comparison
 * @returns {string} - The generated prompt
 */
const generateSizechartComparisonPrompt = (masterSizechart, productSizechart, allowedDeviation, targetCountries = ['US']) => {
  return `
  You are a professional clothing size analyst. I need you to compare a master sizechart template with a product sizechart.
  
  Master Sizechart (${masterSizechart.name}):
  ${JSON.stringify(masterSizechart.columns)}
  ${JSON.stringify(masterSizechart.rows)}
  
  Product Sizechart:
  ${JSON.stringify(productSizechart)}
  
  Target Countries: ${JSON.stringify(targetCountries)}
  Allowed Deviation: ${allowedDeviation}cm
  
  Please analyze the product sizechart and compare it with the master template for the specified target countries. Consider the following:
  
  1. Are all sizes from the master template present in the product sizechart?
  2. Are there any new sizes in the product sizechart that aren't in the master template?
  3. For each measurement, is the difference within the allowed deviation of ${allowedDeviation}cm?
  4. Are there any missing measurements in the product sizechart?
  5. For each target country, analyze the size compatibility and note any country-specific issues.
  
  IMPORTANT: Some measurement values may be ranges (e.g., '34-38' instead of just '34'). When comparing ranges:
  - If both master and product values are ranges, compare their midpoints
  - If one is a range and one is a fixed value, check if the fixed value falls within the range
  - If the fixed value is outside the range but within the allowed deviation of either end of the range, consider it a minor difference
  - If the fixed value is outside the range and beyond the allowed deviation, consider it a major difference
  
  Provide your analysis in the following JSON format:
  {
    "status": "match" | "minor_differences" | "major_differences",
    "summary": "Overall analysis summary",
    "missingMeasurements": [],
    "missingSizes": [],
    "newSizes": [],
    "countrySpecificIssues": {
      "US": "Any US-specific sizing issues",
      "UK": "Any UK-specific sizing issues"
    },
    "deviations": [
      {
        "size": "S",
        "measurement": "Bust",
        "masterValue": 92,
        "productValue": 90,
        "difference": 2,
        "withinTolerance": true
      }
    ],
    "summary": "A brief explanation of the findings"
  }
  `;
};

/**
 * Call OpenAI API for sizechart comparison
 * @param {Object} model - The AI model to use
 * @param {string} prompt - The prompt to send
 * @returns {Promise<string>} - The AI response
 */
const callOpenAI = async (model, prompt) => {
  try {
    const apiKey = model.apiConfig?.apiKey;
    if (!apiKey) {
      throw new Error('OpenAI API key not found');
    }
    
    const response = await fetch(`${model.apiConfig.baseUrl}/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        model: model.modelId,
        messages: [{
          role: 'system',
          content: 'You are a professional clothing size analyst that provides structured JSON responses.'
        }, {
          role: 'user',
          content: prompt
        }],
        temperature: model.temperature || 0.2,
        max_tokens: model.maxTokens || 2000
      })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(`OpenAI API error: ${error.error?.message || 'Unknown error'}`);
    }
    
    const data = await response.json();
    return data.choices[0].message.content;
  } catch (error) {
    console.error('Error calling OpenAI:', error);
    throw error;
  }
};

/**
 * Call DeepSeek API for sizechart comparison
 * @param {Object} model - The AI model to use
 * @param {string} prompt - The prompt to send
 * @returns {Promise<string>} - The AI response
 */
const callDeepSeek = async (model, prompt) => {
  try {
    const apiKey = model.apiConfig?.apiKey;
    if (!apiKey) {
      throw new Error('DeepSeek API key not found');
    }
    
    const response = await fetch(`${model.apiConfig.baseUrl}/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        model: model.modelId,
        messages: [{
          role: 'system',
          content: 'You are a professional clothing size analyst that provides structured JSON responses.'
        }, {
          role: 'user',
          content: prompt
        }],
        temperature: model.temperature || 0.2,
        max_tokens: model.maxTokens || 2000
      })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(`DeepSeek API error: ${error.error?.message || 'Unknown error'}`);
    }
    
    const data = await response.json();
    return data.choices[0].message.content;
  } catch (error) {
    console.error('Error calling DeepSeek:', error);
    throw error;
  }
};

/**
 * Call Gemini API for sizechart comparison
 * @param {Object} model - The AI model to use
 * @param {string} prompt - The prompt to send
 * @returns {Promise<string>} - The AI response
 */
const callGemini = async (model, prompt) => {
  try {
    const apiKey = model.apiConfig?.apiKey;
    if (!apiKey) {
      throw new Error('Gemini API key not found');
    }
    
    const response = await fetch(`${model.apiConfig.baseUrl}/models/${model.modelId}:generateContent?key=${apiKey}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        contents: [{
          role: 'user',
          parts: [{
            text: prompt
          }]
        }],
        generationConfig: {
          temperature: model.temperature || 0.2,
          maxOutputTokens: model.maxTokens || 2000
        }
      })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(`Gemini API error: ${error.error?.message || 'Unknown error'}`);
    }
    
    const data = await response.json();
    return data.candidates[0].content.parts[0].text;
  } catch (error) {
    console.error('Error calling Gemini:', error);
    throw error;
  }
};

/**
 * Parse the AI response into a structured format
 * @param {string} response - The AI response
 * @returns {Object} - The parsed response
 */
const parseAIResponse = (response) => {
  try {
    // Extract JSON from the response
    const jsonMatch = response.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const jsonStr = jsonMatch[0];
      const result = JSON.parse(jsonStr);
      
      // Validate the result structure
      if (!result.status) {
        throw new Error('Invalid response format: missing status');
      }
      
      return result;
    } else {
      throw new Error('Could not extract JSON from AI response');
    }
  } catch (error) {
    console.error('Error parsing AI response:', error);
    // Return a fallback response
    return {
      status: 'error',
      summary: 'Failed to parse AI response. Please try again.',
      error: error.message
    };
  }
};

/**
 * Parse the AI size conversion response into a structured format
 * @param {string} response - The AI response
 * @returns {Object} - The parsed response with converted sizes
 */
const parseSizeConversionResponse = (response) => {
  try {
    // Extract JSON from the response
    const jsonMatch = response.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      let jsonStr = jsonMatch[0];
      
      // Remove comments from the JSON string (like // 34 cm / 2.54)
      jsonStr = jsonStr.replace(/\/\/.*$/gm, ''); // Remove single-line comments
      jsonStr = jsonStr.replace(/\/\*[\s\S]*?\*\//g, ''); // Remove multi-line comments
      
      // Clean up any trailing commas that might be left after removing comments
      jsonStr = jsonStr.replace(/,\s*([\]}])/g, '$1');
      
      const result = JSON.parse(jsonStr);
      
      // Validate the result structure
      if (!result.convertedSizes) {
        throw new Error('Invalid response format: missing convertedSizes');
      }
      
      return result;
    } else {
      throw new Error('Could not extract JSON from AI response');
    }
  } catch (error) {
    console.error('Error parsing AI size conversion response:', error);
    // Return a fallback response
    return {
      status: 'error',
      summary: 'Failed to parse AI response. Please try again.',
      error: error.message,
      convertedSizes: []
    };
  }
};

/**
 * Convert a sizechart to another country's sizing standard using AI
 * @param {string} userId - The user ID
 * @param {string} shopId - The shop ID
 * @param {Object} sourceSizechart - The source sizechart to convert
 * @param {string} targetCountry - The target country code to convert to
 * @param {string} category - The clothing category (women, men, kids)
 * @param {string} modelId - Optional AI model ID to use
 * @returns {Promise<Object>} - The converted sizechart
 */
export const convertSizechartWithAI = async (userId, shopId, sourceSizechart, targetCountry, category, modelId = null) => {
  try {
    // Get the AI model to use
    let model = null;
    
    // First check if we have a model ID from the function call
    if (modelId) {
      model = await getAIModelById(modelId);
    }
    
    // If no model ID was provided or the specified model wasn't found,
    // check if there's a model ID in the shop's sizechart settings
    if (!model && shopId) {
      try {
        const shopSettingsRef = doc(db, `shops/${shopId}/sizechart_settings`, 'settings');
        const settingsDoc = await getDoc(shopSettingsRef);
        
        if (settingsDoc.exists()) {
          const settings = settingsDoc.data();
          if (settings.sizeConversionModelId) {
            model = await getAIModelById(settings.sizeConversionModelId);
          }
        }
      } catch (error) {
        console.error('Error fetching shop sizechart settings:', error);
        // Continue with default model if settings can't be fetched
      }
    }
    
    // Fall back to default model if no other model was found
    if (!model) {
      model = await getDefaultAIModel();
    }
    
    if (!model) {
      throw new Error('No AI model available for size conversion');
    }
    
    // Check if user has enough credits
    const creditCost = model.creditCostPerOutput || 1;
    
    // Temporarily bypass credit check for development/testing
    // In production, uncomment the following lines
    // const hasCredits = await hasEnoughCreditsForAIModel(userId, creditCost);
    // 
    // if (!hasCredits) {
    //   throw new Error(`Not enough credits. You need ${creditCost} credits to perform this size conversion.`);
    // }
    
    console.log(`Using AI model: ${model.name} (${model.provider}) with credit cost: ${creditCost}`);
    
    // Prepare the prompt for the AI model
    const prompt = generateSizeConversionPrompt(sourceSizechart, targetCountry, category);
    
    // Log the request for debugging
    console.log('===== SIZECHART CONVERSION REQUEST =====');
    console.log(`Converting from ${sourceSizechart.countries?.[0] || 'Unknown'} to ${targetCountry} for ${category}`);
    console.log('Source Sizechart:', sourceSizechart);
    console.log('AI Prompt:', prompt);
    
    // Call the appropriate AI provider based on the model
    let result;
    switch (model.provider) {
      case 'openai':
        result = await callOpenAI(model, prompt);
        break;
      case 'deepseek':
        result = await callDeepSeek(model, prompt);
        break;
      case 'gemini':
        result = await callGemini(model, prompt);
        break;
      default:
        throw new Error(`Unsupported AI provider: ${model.provider}`);
    }
    
    // Log the AI response
    console.log('===== SIZECHART CONVERSION RESPONSE =====');
    console.log('Raw AI Response:', result);
    
    // Parse the response
    const parsedResult = parseSizeConversionResponse(result);
    console.log('Parsed Result:', parsedResult);
    
    // Deduct credits for the operation
    await deductCredits(userId, creditCost, {
      shopId,
      type: 'sizechart_conversion',
      modelId: model.id,
      modelName: model.displayName
    });
    
    // Create a new sizechart with the converted sizes
    const convertedSizechart = {
      ...sourceSizechart,
      id: undefined, // Remove ID so a new one will be generated when saved
      name: `${sourceSizechart.name} (${targetCountry})`,
      countries: [targetCountry],
      rows: []
    };
    
    // Map the original sizes to the converted sizes
    sourceSizechart.rows.forEach((row, index) => {
      const convertedSize = parsedResult.convertedSizes[index]?.convertedSize || row.size;
      convertedSizechart.rows.push({
        size: convertedSize,
        values: [...row.values] // Copy the measurement values as they are
      });
    });
    
    return {
      convertedSizechart,
      convertedSizes: parsedResult.convertedSizes || [],
      summary: parsedResult.summary || 'Size conversion completed successfully.',
      notes: parsedResult.notes || []
    };
  } catch (error) {
    console.error('Error converting sizechart with AI:', error);
    throw error;
  }
};

/**
 * Generate a prompt for AI-powered size conversion
 * @param {Object} sourceSizechart - The source sizechart to convert
 * @param {string} targetCountry - The target country code to convert to
 * @param {string} category - The clothing category (women, men, kids)
 * @returns {string} - The generated prompt
 */
const generateSizeConversionPrompt = (sourceSizechart, targetCountry, category) => {
  const sourceCountry = sourceSizechart.countries?.[0] || 'US';
  
  // Let the AI determine the appropriate measurement units for each country
  // instead of hardcoding them
  
  // Get column information to understand what measurements are included
  const measurementColumns = sourceSizechart.columns.filter(col => col.unit !== 'none');
  const measurementNames = measurementColumns.map(col => col.name).join(', ');
  
  return `
  You are a professional clothing size analyst specializing in international size standards. I need you to convert a sizechart from ${sourceCountry} sizing to ${targetCountry} sizing for ${category}'s clothing.
  
  Source Sizechart (${sourceSizechart.name}):
  ${JSON.stringify(sourceSizechart.columns)}
  ${JSON.stringify(sourceSizechart.rows)}
  
  Please analyze the source sizechart and convert each size to the equivalent ${targetCountry} size standard for ${category}'s clothing.
  
  Consider the following in your conversion:
  1. The specific measurements for each size
  2. Standard size equivalents between ${sourceCountry} and ${targetCountry}
  3. Any special sizing considerations for ${category}'s clothing
  4. The full range of sizes, including extended sizes (up to 8XL if present)
  5. IMPORTANT: For UK, US, Canada, Australia, and New Zealand, clothing measurements are typically in INCHES. For EU and most other countries, measurements are typically in CENTIMETERS.
  6. For ${targetCountry} specifically, please use the appropriate measurement units (INCHES for UK, US, CA, AU, NZ and CENTIMETERS for EU and other countries).
  7. IMPORTANT: Detect the units from the source data and convert as needed:
     - If source is in CM and target country uses INCHES: divide by 2.54 to convert to inches
     - If source is in INCHES and target country uses CM: multiply by 2.54 to convert to cm
     Your response should ONLY include the measurements in the appropriate units for the target country.
  8. CRITICAL: If a measurement is given as a range (e.g., "34-35 cm"), maintain the range format in your response (e.g., "13.4-13.8 in"). Do not convert ranges to single values.
  9. The measurements included in this sizechart are: ${measurementNames}

  IMPORTANT: Provide your analysis in the following JSON format WITHOUT ANY COMMENTS in the JSON:
  {
    "summary": "Brief summary of the conversion",
    "notes": ["Any important notes about the conversion, including unit conversions"],
    "convertedSizes": [
      {
        "originalSize": "S",
        "convertedSize": "36",
        "measurements": {
          "bust": "14.2",
          "waist": "11.8"
        },
        "unit": "in",
        "notes": "Any size-specific notes"
      }
    ]
  }
  
  DO NOT include any comments or explanations within the JSON structure itself. The unit field should be either "in" for inches or "cm" for centimeters.
  
  The convertedSizes array should have one entry for each size in the original sizechart, in the same order.
  `;
};

/**
 * Generate a complete sizechart from scratch using AI based on product information
 * @param {string} userId - The user ID
 * @param {string} shopId - The shop ID
 * @param {string} productCategory - The product category (e.g., 'women's dress')
 * @param {string} targetCountry - The target country code to generate for
 * @param {string} responseLanguage - The language for the response
 * @param {string} inputText - Product information or image link
 * @param {string} modelId - Optional AI model ID to use
 * @param {boolean} includeBaseSizes - Whether to include base sizes (S/M/L) along with country-specific sizes
 * @returns {Promise<Object>} - The generated sizechart
 */
export const generateSizechartWithAI = async (userId, shopId, productCategory, targetCountry, responseLanguage, inputText, modelId = null, includeBaseSizes = false, garmentType = null, fitType = null, stretchLevel = null, materialInfo = null) => {
  try {
    // Get the AI model to use
    let model = null;
    if (modelId) {
      model = await getAIModelById(modelId);
    }
    
    if (!model) {
      // Fall back to default model if specified model not found
      model = await getDefaultAIModel();
    }
    
    if (!model) {
      throw new Error('No AI model available for sizechart generation');
    }
    
    // Check if user has enough credits
    const creditCost = model.creditCostPerOutput || 1;
    const hasCredits = await hasEnoughCreditsForAIModel(userId, creditCost);
    
    if (!hasCredits) {
      throw new Error(`Not enough credits. You need ${creditCost} credits to generate this sizechart.`);
    }
    
    // Generate the prompt for sizechart generation
    let promptInstructions = `You are a professional clothing size analyst specializing in international size standards. I need you to create a complete sizechart for a ${productCategory} in ${targetCountry} sizing.`;
    
    // Add garment-specific information if available
    if (garmentType) {
      promptInstructions += `

This is a ${garmentType} type garment.`;
    }
    
    // Always include material info if available for AI to analyze
    if (materialInfo) {
      promptInstructions += `

Material composition: ${materialInfo}`;
    }
    
    // Instructions for AI to analyze and determine fit type and stretch level
    promptInstructions += `

Based on the product details and material composition, please determine:
1. The fit type (slim, regular, or loose)
2. The stretch level (none, slight, moderate, or high)

Include this information in your response as structured data with 'fit_type' and 'stretch_level' fields.

Instead of providing fit information for each size, provide a single general fit description for the entire garment. Only include size-specific notes if there's something unusual or special about a particular size that deviates from the general fit (e.g., 'Size XL runs slightly larger than expected').`;
    
    // If fit type was manually specified, include it (but this should be rare)
    if (fitType) {
      promptInstructions += `

Note: The garment has been manually classified as having a ${fitType} fit type.`;
    }
    
    // If stretch level was manually specified, include it (but this should be rare)
    if (stretchLevel) {
      promptInstructions += `

Note: The fabric has been manually classified as having a ${stretchLevel} stretch level.`;
    }
    
    // Add instructions for including base sizes if requested
    if (includeBaseSizes) {
      promptInstructions += `

IMPORTANT: ONLY use the sizes and measurements provided in the input. DO NOT add extra sizes that weren't in the input. If the input contains sizes (like S, M, L, XL, or any other size system), create a chart that shows both these original sizes AND their equivalent ${targetCountry} sizes. DO NOT split one input size into multiple output sizes.

Extract the measurement columns (bust, waist, hip, etc.) directly from the input - don't assume any specific measurements. Create separate columns for the original sizes and country-specific sizes. The number of rows in your output should EXACTLY match the number of sizes in the input.

For each size in the fit_info section:
1. Analyze how it compares to standard ${targetCountry} sizing
2. Provide a fit description (runs large, runs small, true to size) - use the provided fit type (${fitType || 'not specified'}) as a guide
3. Include specific buyer hints about measurements that might differ from expectations
4. Add body measurement recommendations for each key measurement (bust, waist, hip)
5. Assign an appropriate fit tag (${fitType ? fitType.charAt(0).toUpperCase() + fitType.slice(1) + ' Fit' : 'Regular Fit'})
6. Include specific measurement instructions to help customers measure correctly

This detailed fit information will help customers choose the right size and reduce returns.`;
    } else {
      promptInstructions += `

IMPORTANT: ONLY use the sizes and measurements provided in the input. DO NOT add extra sizes that weren't in the input. Extract the size system and measurement columns (bust, waist, hip, etc.) directly from the input - don't assume any specific measurements or size system. The number of rows in your output should EXACTLY match the number of sizes in the input.

For each size in the fit_info section:
1. Analyze how it compares to standard sizing
2. Provide a fit description (runs large, runs small, true to size) - use the provided fit type (${fitType || 'not specified'}) as a guide
3. Include specific buyer hints about measurements that might differ from expectations
4. Add body measurement recommendations for each key measurement (bust, waist, hip)
5. Assign an appropriate fit tag (${fitType ? fitType.charAt(0).toUpperCase() + fitType.slice(1) + ' Fit' : 'Regular Fit'})
6. Include specific measurement instructions to help customers measure correctly

This detailed fit information will help customers choose the right size and reduce returns.`;
    }
    
    // Create the prompt with conditional sections based on includeBaseSizes
    let columnsSection = includeBaseSizes 
      ? `
    { "key": "original_size", "name": "Size", "unit": "none" },
    { "key": "country_size", "name": "${targetCountry} Size", "unit": "none" },` 
      : `
    { "key": "size", "name": "Size", "unit": "none" },`;
    
    // Just a simple example row format - the AI should extract actual data from input
    let rowsSection = includeBaseSizes 
      ? `
    // THIS IS JUST AN EXAMPLE FORMAT - extract actual sizes and measurements from input
    { "original_size": "Size from input", "country_size": "${targetCountry} size equivalent", "values": ["measurement1", "measurement2", "etc"] }` 
      : `
    // THIS IS JUST AN EXAMPLE FORMAT - extract actual sizes and measurements from input
    { "size": "Size from input", "values": ["measurement1", "measurement2", "etc"] }`;
    
    // Log the target country for reference
    console.log(`Generating size chart for ${targetCountry}...`);
    
    // Create a much simpler prompt that lets the AI determine the appropriate units
    const prompt = `You are a professional clothing size analyst specializing in international size standards. Create a complete size chart for ${productCategory} in ${targetCountry} sizing.

${garmentType ? `This is a ${garmentType} type garment.
` : ''}${materialInfo ? `Material composition: ${materialInfo}
` : ''}
Product Information:
${inputText}

### Dynamic Fit and Stretch Analysis:
1. Analyze the garment type and material composition to determine:
   - The fit type (slim, regular, loose).
   - The stretch level (none, slight, moderate, high).
   - Reflect the **proportion of stretchy vs. rigid materials**.
   
2. Material-Based Logic:
   - If the material includes elastic fibers (like spandex, elastane, lycra), it likely has **moderate to high stretch**.
   - Natural fibers (like cotton, wool) typically have **low to slight stretch** unless combined with elastic materials.
   - Blended fabrics (like polyester with spandex) generally result in a **regular fit with some stretch**.
   - Adapt the fit description dynamically based on the **garment type** and **material proportions**:
     - Tops (sweaters, shirts): Typically **regular fit**, but vary based on stretch.
     - Bottoms (pants, jeans): Can vary between **slim, regular, relaxed**.
     - Outerwear (jackets, coats): **Regular or loose fit**.
     - Stretchy garments (leggings, tights): **Slim or body-hugging fit**.

3. Size Mapping Logic:
   - Dynamically determine the **equivalent size for the target country** based on the **provided measurements** and garment type.
   - Avoid **fixed size mappings**. Always calculate size equivalence from the **input measurements**.
   - If the input lacks specific size equivalence, use the most **common size mapping** based on the garment type and country conventions.

4. Fit Tag Logic:
   - Combine **material blend and size difference** to assign fit tags:
     - Smaller sizes with **slight stretch**: **"Slim Fit".** 
     - Medium sizes with **moderate stretch**: **"Regular Fit".** 
     - Larger sizes with **moderate stretch and more room**: **"Relaxed Regular Fit".** 
   - Use **"Loose Fit"** only if the garment clearly indicates an **oversized or relaxed style**.

5. General Fit Description:
   - Provide a **unique fit description** for each size. 
   - Use phrases that indicate **relative differences** between sizes (e.g., "S is snug around the bust, while M allows for moderate ease").
   - Include **material blend impact** in the description.
   - Do NOT repeat the same fit description for multiple sizes.

### Critical Requirements:
1. The "countries" field in your JSON MUST contain ["${targetCountry}"].
2. Each size MUST have a **unique fit description** - do NOT repeat the same description for all sizes.
3. Extract the **size range** from the input - do NOT assume fixed sizes like S, M, L, XL.
4. ${includeBaseSizes ? `Only use the sizes and measurements provided in the input - DO NOT add extra sizes` : 'Use the size system from the input'}.
5. Analyze the garment type and material to determine the most accurate size mapping for this specific product.
6. DO NOT split one input size into multiple output sizes.

### Unit Conversion:
- If the source uses **CM** and the target country uses **INCHES**: divide by 2.54.
- If the source uses **INCHES** and the target country uses **CM**: multiply by 2.54.
- Preserve range values (e.g., "34-36") without converting to single values.

### JSON Format:
// IMPORTANT: Include these two properties at the top level of your JSON:
// - fit_type: Choose EXACTLY ONE of: "slim", "regular", "loose", or "oversized" based on your analysis
// - stretch_level: Choose EXACTLY ONE of: "none", "slight", "moderate", "stretchy", or "high" based on your analysis
{
  "name": "${targetCountry} ${productCategory} Size Chart",
  "category": "${productCategory}",
  "countries": ["${targetCountry}"],
  "fit_type": "regular",
  "stretch_level": "moderate"
  "columns": [
    ${includeBaseSizes ? 
      `{ "key": "original_size", "name": "Size", "unit": "none" },
    { "key": "country_size", "name": "${targetCountry} Size", "unit": "none" },` : 
      `{ "key": "size", "name": "Size", "unit": "none" },`
    }
    // Add measurement columns with proper format:
    // { "key": "measurement_name", "name": "Display Name", "unit": "in" or "cm" }
  ],
  "rows": [
    // CRITICAL: Measurements MUST be in the values array, not as direct properties
    // Dynamically extract sizes and measurements from the input
    ${includeBaseSizes ? 
      `{ "original_size": "Dynamic size from input", "country_size": "Equivalent size", "values": ["Bust", "Waist"] }` : 
      `{ "size": "Dynamic size from input", "values": ["Bust", "Waist"] }`
    }
    // DO NOT use { bust: "34-36", waist: "26-28" }
    // Instead use { values: ["34-36", "26-28"] }
  ],
  "fit_info": [
    // Unique, dynamically generated fit descriptions based on garment type, size, and material
    {
      ${includeBaseSizes ? 
        `"original_size": "Dynamic size from input",` : 
        `"size": "Dynamic size from input",`
      }
      "fit_description": "Unique fit description based on size, material, and garment type.",
      "buyer_hint": "Hint that reflects the specific characteristics of the size.",
      "fit_tag": "Dynamically determined fit type"
    }
  ],
  "notes": [
    "Take measurements according to the guidelines provided.",
    "Measurement accuracy can vary slightly due to fabric stretch.",
    "Ensure to follow the measuring instructions based on garment type."
  ]
}

${fitType ? `Note: The garment has been manually classified as having a ${fitType} fit type.
` : ''}${stretchLevel ? `Note: The fabric has been manually classified as having a ${stretchLevel} stretch level.
` : ''}

Your response must be valid JSON with no comments. THE FIT_INFO SECTION IS MANDATORY.`;
    
    // Log the prompt for debugging
    console.log('===== SIZE CHART GENERATION PROMPT =====');
    console.log(prompt);
    console.log('======================================');
    
    // Call the appropriate AI provider based on the model
    let result;
    switch (model.provider) {
      case 'openai':
        result = await callOpenAI(model, prompt);
        break;
      case 'deepseek':
        result = await callDeepSeek(model, prompt);
        break;
      case 'gemini':
        result = await callGemini(model, prompt);
        break;
      default:
        throw new Error(`Unsupported AI provider: ${model.provider}`);
    }
    
    // Log the AI response for debugging
    console.log('===== SIZE CHART GENERATION RESPONSE =====');
    console.log(result);
    console.log('========================================');
    
    // Parse the result
    const aiResponse = result;
    const parsedResult = parseSizechartGenerationResponse(aiResponse, targetCountry);
    
    // Deduct credits for the operation
    await deductCredits(userId, creditCost, {
      shopId,
      type: 'sizechart_generation',
      modelId: model.id,
      modelName: model.displayName,
      details: `Generated sizechart for ${productCategory} (${targetCountry})`
    });
    
    // Update shop metrics to track size chart generation
    if (shopId) {
      try {
        const shopRef = doc(db, 'shops', shopId);
        await updateDoc(shopRef, {
          'metrics.sizechartsGenerated': increment(1)
        });
        console.log(`Updated size charts generated count for shop ${shopId}`);
      } catch (error) {
        console.error('Error updating size charts generated count:', error);
        // Don't throw the error here to avoid interrupting the main flow
      }
    }
    
    // Return the generated sizechart
    return parsedResult;
  } catch (error) {
    console.error('Error generating sizechart with AI:', error);
    throw error;
  }
};

/**
 * Parse the AI sizechart generation response into a structured format
 * @param {string} response - The AI response
 * @returns {Object} - The parsed response with the generated sizechart
 */
const parseSizechartGenerationResponse = (response, targetCountry) => {
  try {
    // Extract JSON from the response
    const jsonMatch = response.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      let jsonStr = jsonMatch[0];
      // Remove comments from the JSON string
      jsonStr = jsonStr.replace(/\/\/.*$/gm, '');
      jsonStr = jsonStr.replace(/\/\*[\s\S]*?\*\//g, '');
      jsonStr = jsonStr.replace(/,\s*([\]}])/g, '$1');
      const result = JSON.parse(jsonStr);
      
      // Abbreviate units for consistency in display
      if (result.unit === 'inches') result.unit = 'in';
      if (result.unit === 'centimeters') result.unit = 'cm';
      
      // Enforce correct units for specific countries without hardcoding values
      // This ensures consistency regardless of which AI model is used
      const inchCountries = ['UK', 'US', 'CA', 'AU'];
      const cmCountries = ['EU', 'FR', 'DE', 'IT', 'ES', 'NL', 'CN', 'JP', 'KR'];
      
      // Only enforce if the country is in our known lists
      if (inchCountries.includes(targetCountry)) {
        // UK, US, etc. should use inches
        result.unit = 'in';
      } else if (cmCountries.includes(targetCountry)) {
        // EU, FR, etc. should use cm
        result.unit = 'cm';
      }
      // For other countries, keep whatever the AI provided
      
      if (result.columns && result.columns.length > 0) {
        result.columns.forEach(column => {
          // Convert 'inches' to 'in' and 'centimeters' to 'cm'
          if (column.unit === 'inches') column.unit = 'in';
          if (column.unit === 'centimeters') column.unit = 'cm';
          
          // Rename any column with key 'original_size' to have name 'Size' instead of 'Original Size'
          if (column.key === 'original_size' && column.name === 'Original Size') {
            column.name = 'Size';
          }
          
          // Ensure measurement columns use the same unit as the overall result
          // This ensures consistency across the entire size chart
          if (column.key !== 'original_size' && column.key !== 'country_size' && column.key !== 'size' && column.unit !== 'none') {
            column.unit = result.unit;
          }
        });
      }
      
      // Log the extracted values for debugging
      console.log('Extracted size chart values:', {
        category: result.category,
        country: targetCountry,
        columns: result.columns?.map(c => `${c.key}(${c.unit})`).join(', '),
        sizes: result.rows?.map(r => r.original_size || r.size).join(', ')
      });
      
      // Convert values to match the enforced unit if needed
      if (result.rows && result.rows.length > 0) {
        // Define conversion functions
        const convertCmToInches = (value) => Number((value / 2.54).toFixed(1));
        const convertInchesToCm = (value) => Number((value * 2.54).toFixed(1));
        
        // Get current units from columns before they were standardized
        const originalColumnUnits = {};
        result.columns.forEach(col => {
          if (col.key !== 'original_size' && col.key !== 'country_size' && col.key !== 'size') {
            originalColumnUnits[col.key] = col.unit;
          }
        });
        
        // Convert values in each row if needed
        result.rows.forEach(row => {
          if (row.values && Array.isArray(row.values)) {
            // Get measurement columns (excluding size columns)
            const measurementColumns = result.columns.filter(col => 
              col.key !== 'original_size' && col.key !== 'country_size' && col.key !== 'size'
            );
            
            // Convert each value if needed
            row.values = row.values.map((value, index) => {
              const column = measurementColumns[index];
              const originalUnit = originalColumnUnits[column.key];
              
              // Handle range values (strings like '34-36')
              if (typeof value === 'string' && value.includes('-')) {
                // Extract the range parts
                const rangeParts = value.split('-');
                if (rangeParts.length === 2) {
                  const start = parseFloat(rangeParts[0]);
                  const end = parseFloat(rangeParts[1]);
                  
                  // Only convert if both parts are valid numbers
                  if (!isNaN(start) && !isNaN(end)) {
                    // Convert to inches if needed
                    if (originalUnit === 'cm' && result.unit === 'in') {
                      const convertedStart = convertCmToInches(start);
                      const convertedEnd = convertCmToInches(end);
                      return `${convertedStart}-${convertedEnd}`;
                    }
                    // Convert to cm if needed
                    else if (originalUnit === 'in' && result.unit === 'cm') {
                      const convertedStart = convertInchesToCm(start);
                      const convertedEnd = convertInchesToCm(end);
                      return `${convertedStart}-${convertedEnd}`;
                    }
                  }
                }
                // If we can't convert, return the original value
                return value;
              }
              
              // Handle regular number values
              if (typeof value === 'number') {
                // Convert to inches if needed
                if (originalUnit === 'cm' && result.unit === 'in') {
                  return convertCmToInches(value);
                }
                // Convert to cm if needed
                else if (originalUnit === 'in' && result.unit === 'cm') {
                  return convertInchesToCm(value);
                }
              }
              
              // Return the original value for anything else
              return value;
            });
          }
        });
      }
      
      // Transform the result into the expected format if needed
      const transformedRows = result.rows.map(row => {
        // If values is an object, convert it to an array in the right order
        if (row.values && typeof row.values === 'object' && !Array.isArray(row.values)) {
          const values = [];
          // For each column (except size), get the corresponding value
          result.columns.forEach((col, index) => {
            if (col.key !== 'size') {
              values.push(row.values[col.key] || '');
            }
          });
          return {
            size: row.size,
            values
          };
        }
        return row;
      });
      
      // Extract fit_type and stretch_level from the AI response or use defaults
      const fit_type = result.fit_type || 'regular';
      const stretch_level = result.stretch_level || 'none';
      
      // Log the fit type and stretch level for debugging
      console.log('Extracted fit type and stretch level:', { fit_type, stretch_level });
      
      return {
        sizechart: {
          name: result.name || `${targetCountry} ${result.category} Size Chart`,
          category: result.category,
          countries: [targetCountry], // Always use the targetCountry parameter, not result.country
          columns: result.columns,
          rows: transformedRows,
          notes: result.notes || [],
          fit_info: result.fit_info || [],
          fit_type: fit_type,
          stretch_level: stretch_level
        },
        summary: `Generated sizechart for ${result.category} in ${targetCountry}`,
        unit: result.unit || (result.columns && result.columns.length > 2 ? result.columns[2].unit : 'in')
      };
    } else {
      throw new Error('Could not extract JSON from AI response');
    }
  } catch (error) {
    console.error('Error parsing AI sizechart generation response:', error);
    // Return a fallback response
    return {
      status: 'error',
      summary: 'Failed to parse AI response. Please try again.',
      error: error.message,
      sizechart: null
    };
  }
};
